import { AppVersion, CURRENT_APP_VERSION } from '@core/constants/appVersion';

// Query params added to every cross-app (v2 <-> v3) redirect URL.
//
// - APP_SWITCH_PARAM marks the page load as the result of an app switch, so
//   the receiving app can detect a ping-pong: if it would immediately send the
//   user back, the two apps disagree (different data, misconfiguration) and
//   redirecting again would only loop forever.
// - APP_SWITCH_ORG_PARAM carries the active organization. The selected org is
//   persisted per origin (localStorage), so without it each app may be on a
//   different organization — with different viewer settings — which is
//   exactly the disagreement that causes the loop.
export const APP_SWITCH_PARAM = 'appSwitch';
export const APP_SWITCH_ORG_PARAM = 'appSwitchOrg';
const PENDING_ORG_STORAGE_KEY = 'appSwitchOrganizationId';

let arrivedViaAppSwitch = false;
let suppressedTarget: AppVersion | null = null;

const safeSessionStorage = {
  get: (key: string) => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key: string, value: string) => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Storage unavailable — the org simply won't be carried over.
    }
  },
  remove: (key: string) => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // ignore
    }
  },
};

/**
 * Reads and strips the app-switch params from the current URL. Must run once
 * at startup, before the router reads the location. The organization is kept
 * in sessionStorage (not memory) so it survives a B2C login round-trip.
 */
export const consumeAppSwitchParams = () => {
  const url = new URL(window.location.href);
  const marker = url.searchParams.get(APP_SWITCH_PARAM);
  const organizationId = url.searchParams.get(APP_SWITCH_ORG_PARAM);

  if (marker === null && organizationId === null) {
    return;
  }

  arrivedViaAppSwitch = marker !== null;
  if (organizationId) {
    safeSessionStorage.set(PENDING_ORG_STORAGE_KEY, organizationId);
  }

  url.searchParams.delete(APP_SWITCH_PARAM);
  url.searchParams.delete(APP_SWITCH_ORG_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};

export const getPendingAppSwitchOrganizationId = () =>
  safeSessionStorage.get(PENDING_ORG_STORAGE_KEY);

export const clearPendingAppSwitchOrganizationId = () =>
  safeSessionStorage.remove(PENDING_ORG_STORAGE_KEY);

/**
 * Loop breaker for automatic redirects (AppVersionGuard, ShareLinkRedirect).
 * Call it once the target version has been resolved; returns whether the
 * browser may be sent to `target`.
 *
 * The first resolved decision after arriving through an app switch must agree
 * with the app we landed on. If it doesn't, the redirect is suppressed for as
 * long as the target stays the same; it is released once the target matches
 * this app again (e.g. an admin flips the setting), so later legitimate
 * changes still redirect.
 */
export const allowAutomaticAppSwitch = (target: AppVersion): boolean => {
  if (target === CURRENT_APP_VERSION) {
    arrivedViaAppSwitch = false;
    suppressedTarget = null;
    return false;
  }

  if (arrivedViaAppSwitch) {
    arrivedViaAppSwitch = false;
    suppressedTarget = target;
    console.warn(
      `[AppVersion] Not redirecting back to ${target}: this page was just opened by an app ` +
        'switch from that version. Both apps resolved a different version, so redirecting ' +
        'again would loop.',
    );
  }

  return suppressedTarget !== target;
};
